import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import ShowcaseLayout from './components/ShowcaseLayout';

// Showcase pages
import ShowcaseHome from './pages/Showcase/Home';
import ShowcaseFormations from './pages/Showcase/Formations';
import ShowcaseLogiciel from './pages/Showcase/Logiciel';
import ShowcaseConferences from './pages/Showcase/Conferences';
import ShowcaseShop from './pages/Showcase/Shop';
import ShowcaseEvenements from './pages/Showcase/Evenements';
import { ContactPage, MentionsLegalesPage, CgvPage } from './pages/Showcase/LegalPages';

// Auth pages
import Login from './pages/Login';

// Student pages
import StudentDashboard from './pages/Student/Dashboard';
import StudentCourses from './pages/Student/Courses';
import StudentCourseView from './pages/Student/CourseView';
import StudentCourseList from './pages/Student/CourseList';
import StudentQuizzes from './pages/Student/Quizzes';
import StudentQuizAttempt from './pages/Student/QuizAttempt';
import StudentStats from './pages/Student/Stats';
import StudentMessages from './pages/Student/Messages';
import ModulePlayer from './pages/Student/ModulePlayer';
import StudentProfile from './pages/Student/Profile';

// Professor pages
import ProfessorDashboard from './pages/Professor/Dashboard';
import ProfessorCourses from './pages/Professor/Courses';
import ProfessorCourseList from './pages/Professor/CourseList';
import ProfessorCourseView from './pages/Professor/CourseView';
import ProfessorCourseCreator from './pages/Professor/CourseCreator';
import ProfessorCourseAssignment from './pages/Professor/CourseAssignment';
import ProfessorQuizzes from './pages/Professor/Quizzes';
import ProfessorQuizList from './pages/Professor/QuizList';
import ProfessorQuizView from './pages/Professor/QuizView';
import ProfessorQuizCreator from './pages/Professor/QuizCreator';
import ProfessorQuizEdit from './pages/Professor/QuizEdit';
import ProfessorQuizPreview from './pages/Professor/QuizPreview';
import ProfessorMessages from './pages/Professor/MessageList';
import ProfessorProfile from './pages/Professor/Profile';
import ProfessorCourseViewerPage from './pages/Professor/CourseViewerPage';
import ProfessorCourseViewerEdit from './pages/Professor/CourseViewerEdit';

// Admin pages
import AdminDashboard from './pages/Admin/Dashboard';
import AdminUsers from './pages/Admin/Users';
import AdminCategory from './pages/Admin/AdminCategories';
import AdminCourses from './pages/Admin/Courses';
import AdminCourseCreator from './pages/Admin/CourseCreator';
import AdminCourseEdit from './pages/Admin/CourseEdit';
import AdminCourseView from './pages/Admin/CourseView';
import AdminListQuiz from './pages/Admin/QuizList'
import AdminQuiz from './pages/Admin/Quizzes';
import AdminQuizCreator from './pages/Admin/QuizCreator';
import AdminQuizEdit from './pages/Admin/QuizEdit';
import AdminQuizPreview from './pages/Admin/QuizPreview';
import AdminPayments from './pages/Admin/Payments';
import AdminStats from './pages/Admin/Stats';
import AdminMessages from './pages/Admin/Messages';
import AdminSiteContent from './pages/Admin/SiteContent';
import AdminSiteSettings from './pages/Admin/SiteSettings';
import AdminCustomization from './pages/Admin/Customization';
import AdminCourseAssignment from './pages/Admin/CourseAssignment';
import AdminCourseModuleEdit from './pages/Admin/CourseModuleEdit';
import AdminCourseModuleViewer from './pages/Admin/CourseModuleViewer';
import QuizReview from './pages/Admin/QuizReview';

// Admin Site Content pages
import AdminSiteEvents from './pages/Admin/siteContent/AdminEvents';
import AdminSiteServices from './pages/Admin/siteContent/AdminServices';
import AdminSiteSections from './pages/Admin/siteContent/AdminSiteSections';
import AdminSiteTestimonials from './pages/Admin/siteContent/AdminTestimonials';
import AdminSiteStats from './pages/Admin/siteContent/Adminsitestats';
import AdminSiteProducts from './pages/Admin/siteContent/AdminProducts';
import AdminSiteNews from './pages/Admin/siteContent/AdminNews';
import AdminSiteNavigation from './pages/Admin/siteContent/AdminNavigation';
import AdminSiteAdminplans from './pages/Admin/siteContent/Adminplans.tsx';


function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

function AppContent() {
  const { loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#FFB639]"></div>
        <p className="ml-4 text-gray-700">Loading application...</p>
      </div>
    );
  }

  return (
    <Router>
      <div className="min-h-screen bg-gray-50">
          <Routes>
            {/* Showcase Routes */}
            <Route path="/" element={
              <ShowcaseLayout>
                <ShowcaseHome />
              </ShowcaseLayout>
            } />
            <Route path="/formations" element={
              <ShowcaseLayout>
                <ShowcaseFormations />
              </ShowcaseLayout>
            } />
            <Route path="/logiciel" element={
              <ShowcaseLayout>
                <ShowcaseLogiciel />
              </ShowcaseLayout>
            } />
            <Route path="/conferences" element={
              <ShowcaseLayout>
                <ShowcaseConferences />
              </ShowcaseLayout>
            } />
            <Route path="/shop" element={
              <ShowcaseLayout>
                <ShowcaseShop />
              </ShowcaseLayout>
            } />
            <Route path="/evenements" element={
              <ShowcaseLayout>
                <ShowcaseEvenements />
              </ShowcaseLayout>
            } />

            <Route path="/contact" element={
              <ShowcaseLayout>
                <ContactPage />
              </ShowcaseLayout>
            } />
            <Route path="/mentions-legales" element={
              <ShowcaseLayout>
                <MentionsLegalesPage />
              </ShowcaseLayout>
            } />
            <Route path="/cgv" element={
              <ShowcaseLayout>
                <CgvPage />
              </ShowcaseLayout>
            } />

            {/* Auth Routes */}
            <Route path="/login" element={<Login />} />
            
            {/* Student Routes */}
            <Route path="/student" element={
              <ProtectedRoute allowedRoles={['student']}>
                <Navigate to="/student/dashboard" replace />
              </ProtectedRoute>
            } />
            <Route path="/student/dashboard" element={
              <ProtectedRoute allowedRoles={['student']}>
                <StudentDashboard />
              </ProtectedRoute>
            } />
            <Route path="/student/courses" element={
              <ProtectedRoute allowedRoles={['student']}>
                <StudentCourseList />
              </ProtectedRoute>
            } />
            <Route path="/student/courses/:id" element={
              <ProtectedRoute allowedRoles={['student']}>
                <StudentCourseView />
              </ProtectedRoute>
            } />
            <Route path="/student/quizzes" element={
              <ProtectedRoute allowedRoles={['student']}>
                <StudentQuizzes />
              </ProtectedRoute>
            } />
            <Route path="/student/quiz/:quizId/attempt/:attemptId" element={
              <ProtectedRoute allowedRoles={['student']}>
                <StudentQuizAttempt />
              </ProtectedRoute>
            } />
            
            <Route path="/student/stats" element={
              <ProtectedRoute allowedRoles={['student']}>
                <StudentStats />
              </ProtectedRoute>
            } />
            <Route path="/student/messages" element={
              <ProtectedRoute allowedRoles={['student']}>
                <StudentMessages />
              </ProtectedRoute>
            } />
            <Route path="/student/profile" element={
              <ProtectedRoute allowedRoles={['student']}>
                <StudentProfile />
              </ProtectedRoute>
            } />

            {/* Professor Routes */}
            <Route path="/professor" element={
              <ProtectedRoute allowedRoles={['professor']}>
                <Navigate to="/professor/dashboard" replace />
              </ProtectedRoute>
            } />
            <Route path="/professor/dashboard" element={
              <ProtectedRoute allowedRoles={['professor']}>
                <ProfessorDashboard />
              </ProtectedRoute>
            } />
            <Route path="/professor/courses" element={
              <ProtectedRoute allowedRoles={['professor']}>
                <ProfessorCourseList />
              </ProtectedRoute>
            } />
            <Route path="/professor/courses/:id" element={
              <ProtectedRoute allowedRoles={['professor']}>
                <ProfessorCourseView />
              </ProtectedRoute>
            } />
            <Route path="/professor/courses/create" element={
              <ProtectedRoute allowedRoles={['professor']}>
                <ProfessorCourseCreator />
              </ProtectedRoute>
            } />
            <Route path="/professor/courses/:id/edit" element={
              <ProtectedRoute allowedRoles={['professor']}>
                <ProfessorCourseCreator />
              </ProtectedRoute>
            } />
            <Route path="/professor/courses/:courseId/section/:sectionId/module/:moduleId" element={
              <ProtectedRoute allowedRoles={['professor']}>
                <ProfessorCourseViewerPage />
              </ProtectedRoute>
            } />
            <Route path="/professor/courses/:courseId/section/:sectionId/module/:moduleId/edit" element={
              <ProtectedRoute allowedRoles={['professor']}>
                <ProfessorCourseViewerEdit />
              </ProtectedRoute>
            } />
            <Route path="/professor/courses/:id/assign" element={
              <ProtectedRoute allowedRoles={['professor']}>
                <ProfessorCourseAssignment />
              </ProtectedRoute>
            } />
            <Route path="/professor/quizzes" element={
              <ProtectedRoute allowedRoles={['professor']}>
                <ProfessorQuizList />
              </ProtectedRoute>
            } />
            <Route path="/professor/quizzes/:id" element={
              <ProtectedRoute allowedRoles={['professor']}>
                <ProfessorQuizView />
              </ProtectedRoute>
            } />
            <Route path="/professor/quizzes/create" element={
              <ProtectedRoute allowedRoles={['professor']}>
                <ProfessorQuizCreator />
              </ProtectedRoute>
            } />
            <Route path="/professor/quizzes/:id/edit" element={
              <ProtectedRoute allowedRoles={['professor']}>
                <ProfessorQuizEdit />
              </ProtectedRoute>
            } />
            <Route path="/professor/quizzes/:id/preview" element={
              <ProtectedRoute allowedRoles={['professor']}>
                <ProfessorQuizPreview />
              </ProtectedRoute>
            } />
            <Route path="/professor/messages" element={
              <ProtectedRoute allowedRoles={['professor']}>
                <ProfessorMessages />
              </ProtectedRoute>
            } />
            <Route path="/professor/profile" element={
              <ProtectedRoute allowedRoles={['professor']}>
                <ProfessorProfile />
              </ProtectedRoute>
            } />

            {/* Admin Routes */}
            <Route path="/admin" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <Navigate to="/admin/dashboard" replace />
              </ProtectedRoute>
            } />
            <Route path="/admin/dashboard" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminDashboard />
              </ProtectedRoute>
            } />
            <Route path="/admin/users" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminUsers />
              </ProtectedRoute>
            } />
            <Route path="/admin/category" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminCategory />
              </ProtectedRoute>
            } />
            <Route path="/admin/courses" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminCourses />
              </ProtectedRoute>
            } />
            <Route path="/admin/courses/:id" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminCourseView />
              </ProtectedRoute>
            } />
            <Route path="/admin/courses/creator" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminCourseCreator />
              </ProtectedRoute>
            } />
            <Route path="/admin/courses/:courseId/edit" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminCourseEdit />
              </ProtectedRoute>
            } />
            <Route path="/admin/courses/:id/assign" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminCourseAssignment />
              </ProtectedRoute>
            } />
                        {/* Fix 17/09/2026 : lecteur de module accessible aux étudiants */}
            <Route path="/student/courses/:id/section/:sectionId/module/:moduleId" element={
              <ProtectedRoute allowedRoles={['student']}>
                <ModulePlayer />
              </ProtectedRoute>
            } />
            <Route path="/admin/courses/:courseId/section/:sectionId/module/:moduleId" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminCourseModuleViewer />
              </ProtectedRoute>
            } />
            <Route path="/admin/courses/:courseId/section/:sectionId/module/:moduleId/edit" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminCourseModuleEdit />
              </ProtectedRoute>
            } />
            <Route path="/admin/quizzes" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminListQuiz />
              </ProtectedRoute>
            } />
            <Route path="/admin/quizzes/:id/creator" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminQuizCreator />
              </ProtectedRoute>
            } />
            <Route path="/admin/quizzes/:id" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminQuiz />
              </ProtectedRoute>
            } />
            <Route path="/admin/quizzes/:id/edit" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminQuizEdit />
              </ProtectedRoute>
            } />
             <Route path="/admin/quizzes/:id/preview" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminQuizPreview />
              </ProtectedRoute>
            } />
             <Route path="/admin/quizzes/:quizId/admin-preview" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminQuizPreview />
              </ProtectedRoute>
            } />
            <Route path="/admin/quizzes/review/:attemptId" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <QuizReview />
              </ProtectedRoute>
            } />
            <Route path="/admin/payments" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminPayments />
              </ProtectedRoute>
            } />
            <Route path="/admin/stats" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminStats />
              </ProtectedRoute>
            } />
            <Route path="/admin/messages" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminMessages />
              </ProtectedRoute>
            } />
            <Route path="/admin/site-content" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminSiteContent />
              </ProtectedRoute>
            } />
            <Route path="/admin/site-settings" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminSiteSettings />
              </ProtectedRoute>
            } />
            <Route path="/admin/customization" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminCustomization />
              </ProtectedRoute>
            } />
            <Route path="/admin/site-sections" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminSiteSections />
              </ProtectedRoute>
            } />
            <Route path="/admin/site-services" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminSiteServices />
              </ProtectedRoute>
            } />
            <Route path="/admin/site-events" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminSiteEvents />
              </ProtectedRoute>
            } />
            <Route path="/admin/site-testimonials" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminSiteTestimonials />
              </ProtectedRoute>
            } />
            <Route path="/admin/site-stats" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminSiteStats />
              </ProtectedRoute>
            } />
            <Route path="/admin/site-products" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminSiteProducts />
              </ProtectedRoute>
            } />
            <Route path="/admin/site-news" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminSiteNews />
              </ProtectedRoute>
            } />
            <Route path="/admin/site-navigation" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminSiteNavigation />
              </ProtectedRoute>
            } />
            <Route path="/admin/site-plans" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminSiteAdminplans />
              </ProtectedRoute>
            } />
          </Routes>
          <Toaster position="top-right" />
        </div>
      </Router>
    
  );
}

export default App;